// Page de gestion des Devis - Espace Pro
// Avec génération PDF et signature électronique

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Plus, Search, Filter, Eye, Edit, Trash2, Send, Copy,
  Download, CheckCircle2, Clock, X, AlertCircle, XCircle, Receipt,
  User, Building2, Euro, Calendar, Pen, ChevronDown, MoreVertical, Share2, Link2, ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { devisAPI, clientsAPI } from '../../services/sellerAPI';

import { API_URL } from '../../config/api';

// Statuts devis
const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700', icon: Edit },
  sent: { label: 'Envoyé', color: 'bg-blue-100 text-blue-700', icon: Send },
  viewed: { label: 'Consulté', color: 'bg-indigo-100 text-indigo-700', icon: Eye },
  accepted: { label: 'Accepté', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700', icon: XCircle },
  expired: { label: 'Expiré', color: 'bg-orange-100 text-orange-700', icon: Clock },
  invoiced: { label: 'Facturé', color: 'bg-purple-100 text-purple-700', icon: Receipt },
};

// Carte devis
function DevisCard({ devis, onEdit, onDelete, onDuplicate, onSend, onConvert, onDownloadPDF, onShareLink }) {
  const [showMenu, setShowMenu] = useState(false);
  const status = STATUS_CONFIG[devis.status] || STATUS_CONFIG.draft;
  const StatusIcon = status.icon;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  const isExpired = devis.valid_until && new Date(devis.valid_until) < new Date() && devis.status === 'sent';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
            <FileText size={20} className="text-orange-600" />
          </div>
          <div>
            <p className="font-bold text-gray-900">{devis.devis_number}</p>
            <p className="text-xs text-gray-500">{formatDate(devis.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
            <StatusIcon size={12} />
            {status.label}
          </span>
          {isExpired && (
            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
              Expiré
            </span>
          )}
        </div>
      </div>

      {/* Contenu */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-1">{devis.title}</h3>
        
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
          <Building2 size={14} className="text-gray-400" />
          <span className="truncate">{devis.client_name}</span>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Montant TTC</p>
            <p className="text-lg font-bold text-gray-900">{devis.total_ttc?.toFixed(2)} €</p>
          </div>
          <div>
            <p className="text-gray-500">Validité</p>
            <p className="font-medium text-gray-700">{formatDate(devis.valid_until)}</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 p-4 border-t border-gray-100 bg-gray-50">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDownloadPDF(devis)}
          className="rounded-lg"
        >
          <Download size={14} className="mr-1" /> PDF
        </Button>
        
        {devis.status === 'draft' && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(devis)}
              className="rounded-lg"
            >
              <Edit size={14} className="mr-1" /> Modifier
            </Button>
            <Button
              size="sm"
              onClick={() => onSend(devis)}
              className="rounded-lg bg-blue-500 hover:bg-blue-600"
            >
              <Send size={14} className="mr-1" /> Envoyer
            </Button>
          </>
        )}
        
        {devis.status === 'accepted' && !devis.facture_id && (
          <Button
            size="sm"
            onClick={() => onConvert(devis)}
            className="rounded-lg bg-green-500 hover:bg-green-600"
          >
            <Receipt size={14} className="mr-1" /> Facturer
          </Button>
        )}

        <div className="relative ml-auto">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <MoreVertical size={16} className="text-gray-500" />
          </button>
          
          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="absolute right-0 bottom-full mb-1 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[160px] z-10"
              >
                <button
                  onClick={() => { onShareLink(devis); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50"
                >
                  <Share2 size={14} /> Partager le lien
                </button>
                <button
                  onClick={() => { onDuplicate(devis); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Copy size={14} /> Dupliquer
                </button>
                {devis.status === 'draft' && (
                  <button
                    onClick={() => { onDelete(devis); setShowMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} /> Supprimer
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}

// Formulaire de création de devis
function DevisForm({ devis, clients, onSave, onClose }) {
  const [formData, setFormData] = useState({
    client_id: devis?.client_id || '',
    title: devis?.title || '',
    description: devis?.description || '',
    lines: devis?.lines || [{ description: '', quantity: 1, unit: 'unité', unit_price: 0, tva_rate: 10 }],
    validity_days: devis?.validity_days || 30,
    payment_terms: devis?.payment_terms || 'Paiement à réception',
    notes: devis?.notes || '',
    global_discount_percent: devis?.global_discount_percent || 0
  });
  const [saving, setSaving] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({
    type: 'individual',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: '',
    address_line1: '',
    city: '',
    postal_code: ''
  });

  const addLine = () => {
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, { description: '', quantity: 1, unit: 'unité', unit_price: 0, tva_rate: 10 }]
    }));
  };

  const updateLine = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.map((line, i) => i === index ? { ...line, [field]: value } : line)
    }));
  };

  const removeLine = (index) => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    let subtotalHT = 0;
    let totalTVA = 0;
    
    formData.lines.forEach(line => {
      const lineSubtotal = (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0);
      const lineTVA = lineSubtotal * ((parseFloat(line.tva_rate) || 0) / 100);
      subtotalHT += lineSubtotal;
      totalTVA += lineTVA;
    });
    
    const discountAmount = subtotalHT * (formData.global_discount_percent / 100);
    subtotalHT -= discountAmount;
    const totalTTC = subtotalHT + totalTVA;
    
    return { subtotalHT, totalTVA, totalTTC };
  };

  const totals = calculateTotals();

  const handleCreateClient = async () => {
    try {
      const result = await clientsAPI.create(newClient);
      toast.success('Client créé');
      setFormData(prev => ({ ...prev, client_id: result.client_id }));
      setShowNewClient(false);
      // Refresh client list would be needed here in real app
    } catch (e) {
      toast.error('Erreur lors de la création du client');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    if (!formData.title) {
      toast.error('Titre requis');
      return;
    }
    if (formData.lines.length === 0 || !formData.lines[0].description) {
      toast.error('Au moins une ligne est requise');
      return;
    }

    setSaving(true);
    try {
      if (devis?.id) {
        await devisAPI.update(devis.id, formData);
        toast.success('Devis mis à jour');
      } else {
        await devisAPI.create(formData);
        toast.success('Devis créé');
      }
      onSave();
    } catch (e) {
      toast.error(e.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden my-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-orange-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
              <FileText size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {devis ? 'Modifier le devis' : 'Nouveau devis'}
              </h2>
              <p className="text-sm text-gray-500">Crée un devis professionnel en quelques clics</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-orange-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(95vh-180px)]">
          <div className="p-6 space-y-6">
            
            {/* Client */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <User size={18} className="text-gray-400" />
                  Client
                </h3>
                <button
                  type="button"
                  onClick={() => setShowNewClient(!showNewClient)}
                  className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                >
                  + Nouveau client
                </button>
              </div>
              
              {showNewClient ? (
                <div className="space-y-3 bg-white rounded-lg p-4 border border-gray-200">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Prénom"
                      value={newClient.first_name}
                      onChange={(e) => setNewClient({...newClient, first_name: e.target.value})}
                    />
                    <Input
                      placeholder="Nom"
                      value={newClient.last_name}
                      onChange={(e) => setNewClient({...newClient, last_name: e.target.value})}
                    />
                  </div>
                  <Input
                    placeholder="Entreprise (optionnel)"
                    value={newClient.company_name}
                    onChange={(e) => setNewClient({...newClient, company_name: e.target.value})}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      type="email"
                      placeholder="Email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                    />
                    <Input
                      placeholder="Téléphone"
                      value={newClient.phone}
                      onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" onClick={handleCreateClient} className="bg-green-500 hover:bg-green-600">
                      Créer le client
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowNewClient(false)}>
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <select
                  value={formData.client_id}
                  onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white"
                  required
                >
                  <option value="">Sélectionner un client...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.company_name || `${client.first_name} ${client.last_name}`} - {client.email}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Objet */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Objet du devis *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Ex: Rénovation salle de bain"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Description détaillée du devis..."
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl"
              />
            </div>

            {/* Lignes du devis */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">Détail du devis</h3>
                <button
                  type="button"
                  onClick={addLine}
                  className="text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1"
                >
                  <Plus size={16} /> Ajouter une ligne
                </button>
              </div>
              
              <div className="space-y-3">
                {formData.lines.map((line, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-4">
                    <div className="grid grid-cols-12 gap-3 items-start">
                      <div className="col-span-12 lg:col-span-5">
                        <Input
                          placeholder="Description *"
                          value={line.description}
                          onChange={(e) => updateLine(index, 'description', e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-span-3 lg:col-span-1">
                        <Input
                          type="number"
                          placeholder="Qté"
                          value={line.quantity}
                          onChange={(e) => updateLine(index, 'quantity', parseFloat(e.target.value))}
                          min="0"
                          step="0.1"
                        />
                      </div>
                      <div className="col-span-3 lg:col-span-2">
                        <select
                          value={line.unit}
                          onChange={(e) => updateLine(index, 'unit', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm"
                        >
                          <option value="unité">unité</option>
                          <option value="heure">heure</option>
                          <option value="jour">jour</option>
                          <option value="m²">m²</option>
                          <option value="ml">ml</option>
                          <option value="forfait">forfait</option>
                        </select>
                      </div>
                      <div className="col-span-3 lg:col-span-2">
                        <Input
                          type="number"
                          placeholder="Prix HT"
                          value={line.unit_price}
                          onChange={(e) => updateLine(index, 'unit_price', parseFloat(e.target.value))}
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div className="col-span-2 lg:col-span-1">
                        <select
                          value={line.tva_rate}
                          onChange={(e) => updateLine(index, 'tva_rate', parseFloat(e.target.value))}
                          className="w-full px-2 py-2 border border-gray-200 rounded-lg bg-white text-sm"
                        >
                          <option value="20">20%</option>
                          <option value="10">10%</option>
                          <option value="5.5">5.5%</option>
                          <option value="0">0%</option>
                        </select>
                      </div>
                      <div className="col-span-1 flex items-center justify-end">
                        {formData.lines.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLine(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 text-right text-sm text-gray-500">
                      Sous-total: <span className="font-medium text-gray-900">
                        {((parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0)).toFixed(2)} € HT
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totaux */}
            <div className="bg-orange-50 rounded-xl p-4">
              <div className="flex flex-col items-end space-y-2">
                <div className="flex items-center gap-4">
                  <span className="text-gray-600">Remise globale:</span>
                  <div className="w-24">
                    <Input
                      type="number"
                      value={formData.global_discount_percent}
                      onChange={(e) => setFormData({...formData, global_discount_percent: parseFloat(e.target.value) || 0})}
                      min="0"
                      max="100"
                      className="text-right"
                    />
                  </div>
                  <span>%</span>
                </div>
                <div className="flex items-center justify-between w-64 border-t border-orange-200 pt-2">
                  <span className="text-gray-600">Total HT:</span>
                  <span className="font-medium">{totals.subtotalHT.toFixed(2)} €</span>
                </div>
                <div className="flex items-center justify-between w-64">
                  <span className="text-gray-600">Total TVA:</span>
                  <span className="font-medium">{totals.totalTVA.toFixed(2)} €</span>
                </div>
                <div className="flex items-center justify-between w-64 border-t border-orange-200 pt-2">
                  <span className="font-bold text-gray-900">Total TTC:</span>
                  <span className="text-xl font-bold text-orange-600">{totals.totalTTC.toFixed(2)} €</span>
                </div>
              </div>
            </div>

            {/* Conditions */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Validité (jours)</label>
                <Input
                  type="number"
                  value={formData.validity_days}
                  onChange={(e) => setFormData({...formData, validity_days: parseInt(e.target.value)})}
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Conditions de paiement</label>
                <Input
                  value={formData.payment_terms}
                  onChange={(e) => setFormData({...formData, payment_terms: e.target.value})}
                  placeholder="Paiement à réception"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes (visibles sur le devis)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Informations complémentaires..."
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl"
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            Total TTC: <span className="font-bold text-lg text-gray-900">{totals.totalTTC.toFixed(2)} €</span>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {saving ? 'Enregistrement...' : devis ? 'Enregistrer' : 'Créer le devis'}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function DevisPage() {
  const navigate = useNavigate();
  const [devisList, setDevisList] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDevis, setEditingDevis] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [devisData, clientsData] = await Promise.all([
        devisAPI.getAll(statusFilter || null),
        clientsAPI.getAll()
      ]);
      setDevisList(devisData);
      setClients(clientsData);
    } catch (e) {
      console.error('Error fetching data:', e);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const handleDownloadPDF = (devis) => {
    const token = localStorage.getItem('buyer_token');
    const url = `${API_URL}/api/compte/devis/${devis.id}/pdf`;
    
    // Ouvrir dans un nouvel onglet
    window.open(url + `?token=${token}`, '_blank');
  };

  const handleSend = async (devis) => {
    if (!window.confirm('Envoyer ce devis au client par email ?')) return;
    
    try {
      const result = await devisAPI.send(devis.id);
      if (result.email_sent) {
        toast.success('Devis envoyé par email !', {
          description: 'Le client a reçu un email avec le lien vers son espace.'
        });
      } else {
        toast.success('Devis marqué comme envoyé', {
          description: 'Email non envoyé (service non configuré). Partagez le lien manuellement.'
        });
      }
      fetchData();
    } catch (e) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const handleDuplicate = async (devis) => {
    try {
      const result = await devisAPI.duplicate(devis.id);
      toast.success(`Devis dupliqué: ${result.devis_number}`);
      fetchData();
    } catch (e) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleConvert = async (devis) => {
    if (!window.confirm('Convertir ce devis en facture ?')) return;
    
    try {
      const result = await devisAPI.convertToInvoice(devis.id);
      toast.success(`Facture créée: ${result.facture_number}`);
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Erreur lors de la conversion');
    }
  };

  const handleDelete = async (devis) => {
    if (!window.confirm('Supprimer ce devis ?')) return;
    
    try {
      await devisAPI.delete(devis.id);
      toast.success('Devis supprimé');
      fetchData();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleShareLink = async (devis) => {
    try {
      const token = localStorage.getItem('buyer_token');
      const response = await fetch(`${API_URL}/api/compte/devis/${devis.id}/generate-portal-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Erreur');
      
      const result = await response.json();
      const fullUrl = `${window.location.origin}${result.url}`;
      
      // Copier dans le presse-papier
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Lien copié dans le presse-papier !', {
        description: 'Envoie ce lien à ton client pour qu\'il puisse voir et signer ses devis.'
      });
    } catch (e) {
      toast.error('Erreur lors de la génération du lien');
    }
  };

  const handleSave = () => {
    setShowForm(false);
    setEditingDevis(null);
    fetchData();
  };

  const filteredDevis = devisList.filter(d =>
    searchQuery === '' ||
    d.devis_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats
  const stats = {
    total: devisList.length,
    draft: devisList.filter(d => d.status === 'draft').length,
    sent: devisList.filter(d => d.status === 'sent').length,
    accepted: devisList.filter(d => d.status === 'accepted').length,
    totalAmount: devisList.reduce((sum, d) => sum + (d.total_ttc || 0), 0)
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="devis-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mes devis</h1>
          <p className="text-gray-500">{stats.total} devis • {stats.totalAmount.toFixed(2)} € total</p>
        </div>
        <Button 
          onClick={() => { setEditingDevis(null); setShowForm(true); }}
          className="bg-orange-500 hover:bg-orange-600 rounded-xl"
        >
          <Plus size={18} className="mr-2" />
          Nouveau devis
        </Button>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-gray-500">Total</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
          <p className="text-2xl font-bold text-gray-700">{stats.draft}</p>
          <p className="text-sm text-gray-500">Brouillons</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <p className="text-2xl font-bold text-blue-700">{stats.sent}</p>
          <p className="text-sm text-blue-600">Envoyés</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
          <p className="text-2xl font-bold text-green-700">{stats.accepted}</p>
          <p className="text-sm text-green-600">Acceptés</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Rechercher un devis..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm"
          >
            <option value="">Tous les statuts</option>
            <option value="draft">Brouillons</option>
            <option value="sent">Envoyés</option>
            <option value="accepted">Acceptés</option>
            <option value="rejected">Refusés</option>
            <option value="invoiced">Facturés</option>
          </select>
        </div>
      </div>

      {/* Liste des devis */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
        </div>
      ) : filteredDevis.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="font-bold text-gray-900 mb-2">Aucun devis</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery || statusFilter 
              ? "Aucun devis ne correspond à tes critères"
              : "Crée ton premier devis pour tes clients"}
          </p>
          <Button 
            onClick={() => { setEditingDevis(null); setShowForm(true); }}
            className="bg-orange-500 hover:bg-orange-600 rounded-xl"
          >
            <Plus size={18} className="mr-2" />
            Créer un devis
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDevis.map((devis) => (
            <DevisCard
              key={devis.id}
              devis={devis}
              onEdit={(d) => { setEditingDevis(d); setShowForm(true); }}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onSend={handleSend}
              onConvert={handleConvert}
              onDownloadPDF={handleDownloadPDF}
              onShareLink={handleShareLink}
            />
          ))}
        </div>
      )}

      {/* Modal formulaire */}
      <AnimatePresence>
        {showForm && (
          <DevisForm
            devis={editingDevis}
            clients={clients}
            onSave={handleSave}
            onClose={() => { setShowForm(false); setEditingDevis(null); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
